import { useEffectEvent, useMemo } from 'react'
import { MutationHookOptions, OperationVariables, QueryHookOptions, QueryResult, TypedDocumentNode } from '@apollo/client'

import { Entity, ServiceName } from '@/types'

import { showErrorToast } from '@/libraries/ui/hooks'
import { useTranslation } from '@/i18n'

import { FetchResult, MutationResult, useMutation, useQuery } from './apollo'
import { appendToListQuery, filterRemovedFromListQuery } from './apolloCache'
import { getSingleValue, ValueOf } from './apolloUtils'
import { emitServiceEvent, EventName, useServiceListEvents } from './serviceEvents'

type ItemOf<T> = T extends (infer U)[] ? U : never

export function entityListQueryHook<T extends Record<number, Entity[]>, V extends OperationVariables>(
  service: ServiceName, compiledQuery: TypedDocumentNode<T, V>,
  options?: {
    filterCreated?: (data: ItemOf<ValueOf<T>>, variables: V | undefined) => boolean
    refetchOnUpdate?: (old: ItemOf<ValueOf<T>> | undefined, updated: ItemOf<ValueOf<T>>, variables?: V) => boolean
  },
): (...args: OptionalIfEmptyObject<V>) => [
  ValueOf<T>,
  QueryResult<T, V>,
] {
  return (...args: OptionalIfEmptyObject<V>) => {
    const [variables] = args
    const onUpdate = useEffectEvent((updated) => {
      if (options?.refetchOnUpdate) {
        const old = (data as Entity[])?.find(item => item._id === updated._id)
        if (options.refetchOnUpdate(old as ItemOf<ValueOf<T>>, updated, variables)) {
          result.refetch()
        }
      }
    })
    const callbacks = useMemo(() => ({
      created: (data: Entity) => {
        if (options?.filterCreated && !options.filterCreated(data as ItemOf<ValueOf<T>>, variables)) return
        appendToListQuery(compiledQuery, data, variables)
      },
      updated: onUpdate,
      removed: () => filterRemovedFromListQuery(compiledQuery, variables),
    }), [variables])
    // TODO: type this
    useServiceListEvents(service, callbacks)

    const result = useQuery<T, V>(compiledQuery, { fetchPolicy: 'cache-and-network', variables })
    const empty = [] as ValueOf<T>
    const data = result.data !== undefined
      ? getSingleValue(result.data)
      : empty

    return [
      data, result,
    ]
  }
}

type OptionalIfEmptyObject<V> = V[keyof V] extends never ? [] : [V]

export function entityCreateHook<T, V>(
  service: ServiceName, query: TypedDocumentNode<T, V>, options = {},
) {
  return makeMutationHook<T, V>(query, {
    ...options,
    fireEvent: [service, 'created'],
  })
}

export function entityUpdateHook<T, V>(
  service: ServiceName, query: TypedDocumentNode<T, V>, options = {},
) {
  return makeMutationHook<T, V>(query, {
    ...options,
    fireEvent: [service, 'updated'],
  })
}

export function entityDeleteHook<T, V>(
  service: ServiceName, query: TypedDocumentNode<T, V>, options = {},
) {
  return makeMutationHook<T, V>(query, {
    ...options,
    fireEvent: [service, 'removed'],
  })
}

interface MutateHookOptions<T, V> extends MutationHookOptions<T, V> {
  fireEvent?: [ServiceName, EventName]
}

export function makeMutationHook<T, V>(
  query: TypedDocumentNode<T, V>,
  options?: MutateHookOptions<T, V>,
): (args?: MutationHookOptions<T, V>) => [(vars: V) => Promise<FetchResult<T>>, MutationResult<T>] {
  const { onCompleted, onError, fireEvent } = options ?? {}
  return (args = {}) => {
    const operationFailed = useTranslation('common.operationFailed')
    const options: MutationHookOptions<T, V> = {
      onError: onError ?? (err => {
        showErrorToast(operationFailed, err)
        throw err.cause
      }),
      ...args,
      onCompleted: (data) => {
        if (fireEvent) {
          const [service, eventName] = fireEvent
          const value = getSingleValue(data as object)
          emitServiceEvent(service, eventName, value)
        }
        if (onCompleted) onCompleted(data)
        if (args.onCompleted) args.onCompleted(data)
      },
    }
    const [runQuery, data] = useMutation<T, V>(query, options)

    return [
      (variables) => runQuery({ variables }),
      data,
    ]
  }
}

export function backendQueryHook<T, V extends OperationVariables>(
  query: TypedDocumentNode<T, V>,
  additionalCode?: (res: QueryResult<T, V>) => unknown,
): BackendQueryHook<T, V> {
  const useHook = (variables: V, options: QueryHookOptions<T, V> = {}) => {
    const queryResult = useQuery<T, V>(query, { variables, fetchPolicy: 'cache-and-network', ...options })
    if (additionalCode) additionalCode(queryResult)
    return queryResult
  }
  useHook.query = query
  return useHook
}

interface BackendQueryHook<T, V extends OperationVariables> {
  (variables: V, options?: QueryHookOptions<T, V>): QueryResult<T, V>
  query: TypedDocumentNode<T, V>
}
